import React from "react";
//#region types
import { DialogEnums, StyleEnums } from "models/enums";
import { DialogProps } from "models/propTypes/dialogPropTypes";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import { LocalizationHelperInstance } from "utils/helpers";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import CreateAllowanceFormContainer from "./allowanceFormContainer";
import { Grid } from "@mui/material";
import { RoundButton, Icon, useFloatingMessages } from "components/common";
import { useSalaryDeviationsService } from "serviceHooks";
import useIsReadOnly from "./useIsReadOnly";
//#endregion
import cssExports from "theme/styles/main.module.scss";

function useCreateAllowanceDialog(onAllowanceSubmitSuccess?: () => void) {
  const salaryDeviationsService = useSalaryDeviationsService();
  const { enqueueMessage } = useFloatingMessages();

  const { getIsReadOnly } = useIsReadOnly();

  const onDelete = async (closeDialog: () => void, salaryDeviationId: number) => {
    salaryDeviationsService.deleteSalaryDeviation(salaryDeviationId).then((res) => {
      closeDialog();
      enqueueMessage(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.DeleteAllowanceSuccess),
        StyleEnums.DisplayType.Success
      );
      onAllowanceSubmitSuccess && onAllowanceSubmitSuccess();
    });
  };

  const getAllowanceDialog = (options?: {
    salaryDeviationId?: number;
    deviationTypes?: WintSuperkollAPIModel.SalaryDeviationType[];
    monthState?: WintSuperkollAPIModel.SalaryDeviationMonthState;
    yearMonth?: number;
    personId?: number;
    personName?: string;
    isAdminOnlyType?: boolean;
  }): DialogProps => {
    const isReadOnly = getIsReadOnly({
      isAdminOnlyType: options?.isAdminOnlyType ?? false,
      isMonthDisabled: salaryDeviationsService.isMonthDisabled(options?.monthState),
    });

    return {
      isOpen: false,
      title: LocalizationHelperInstance.getFormattedMessage(
        options?.salaryDeviationId
          ? TranslationKeys.SalaryDeviation.EditAllowance
          : TranslationKeys.SalaryDeviation.CreateAllowance
      ),
      titleIcon: IconDefinitionConstants.WMoney,
      actions: !isReadOnly
        ? [
            {
              text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Send),
              successMessage: TranslationKeys.SalaryDeviation.CreatedAllowanceSuccess,
            },
          ]
        : undefined,
      footer:
        options?.salaryDeviationId && !isReadOnly
          ? (closeDialog, isValid) => (
              <Grid item xs alignItems="flex-end">
                <Grid item>
                  <RoundButton
                    className={cssExports.delete}
                    onClick={onDelete.bind(null, closeDialog, options.salaryDeviationId!)}
                    disabled={!isValid}
                  >
                    <Icon icon={IconDefinitionConstants.WTrashAlt} />
                  </RoundButton>
                </Grid>
              </Grid>
            )
          : undefined,
      dialogType: DialogEnums.DialogType.Content,
      children: (
        <CreateAllowanceFormContainer
          salaryDeviationId={options?.salaryDeviationId}
          deviationTypes={options?.deviationTypes}
          monthState={options?.monthState}
          yearMonth={options?.yearMonth}
          personId={options?.personId}
          personName={options?.personName}
        />
      ),
      maxWidth: "sm",
      onSubmit: onAllowanceSubmitSuccess,
    };
  };

  return getAllowanceDialog;
}

export default useCreateAllowanceDialog;
